import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Percent, Truck, Settings2, UserPlus, Receipt } from "lucide-react";
import { api, gbp } from "../lib/api";

const inputCls = "h-12 px-3 rounded-md border border-sand bg-white text-base font-mono-num focus:outline-none focus:ring-2 focus:ring-tea-hover/70 w-full";

export default function QuotePage() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState({});
  const [guestCount, setGuestCount] = useState(80);
  const [marginPct, setMarginPct] = useState(40);
  const [travelCost, setTravelCost] = useState(0);
  const [staffCount, setStaffCount] = useState(0);
  const [staffHours, setStaffHours] = useState(0);
  const [staffRate, setStaffRate] = useState(0);
  const [result, setResult] = useState(null);
  const [labourRate, setLabourRate] = useState(null);

  useEffect(() => {
    api.get("/recipes").then((r) => setRecipes(r.data));
    api.get("/settings").then((r) => { setSettings(r.data); setMarginPct(r.data.default_margin_pct); });
  }, []);

  const items = useMemo(
    () => Object.entries(selected).filter(([, v]) => v.on).map(([id, v]) => ({ recipe_id: id, portions: parseInt(v.portions) || 0 })),
    [selected]
  );

  useEffect(() => {
    if (items.length === 0) { setResult(null); return; }
    const t = setTimeout(() => {
      api.post("/quote/price-floor", {
        items, guest_count: parseInt(guestCount) || 0,
        margin_pct: parseFloat(marginPct) || 0, travel_cost: parseFloat(travelCost) || 0,
        staff_count: parseInt(staffCount) || 0, staff_hours: parseFloat(staffHours) || 0,
        staff_rate_per_hour: parseFloat(staffRate) || 0,
      }).then((r) => setResult(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [items, guestCount, marginPct, travelCost, staffCount, staffHours, staffRate, settings?.labour_rate_per_hour]);

  const turnIntoInvoice = () => {
    const menu = result.items.map((it) => it.name).join(", ");
    navigate("/invoices", {
      state: {
        prefill: {
          description: `${menu} for ${guestCount} guests`,
          amount: result.price_floor,
          total_cost: result.total_cost,
          margin_pct: result.margin_pct,
        },
      },
    });
  };

  const toggle = (id) => {
    setSelected((s) => ({ ...s, [id]: { on: !s[id]?.on, portions: s[id]?.portions ?? guestCount } }));
  };

  const saveLabourRate = async () => {
    const { data } = await api.put("/settings", { labour_rate_per_hour: parseFloat(labourRate) || 0, default_margin_pct: parseFloat(marginPct) || 40 });
    setSettings(data);
    setLabourRate(null);
    api.get("/recipes").then((r) => setRecipes(r.data));
  };

  if (!recipes || !settings) return <p className="text-ink/50 py-10">Loading…</p>;

  return (
    <div className="space-y-6 animate-rise" data-testid="quote-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">The number that matters</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-1">Price floor</h1>
        <p className="text-ink/60 mt-2 text-sm sm:text-base">The minimum you can charge without losing money, recalculated live from your latest receipt prices.</p>
      </div>

      {recipes.length === 0 ? (
        <div className="bg-surface border border-dashed border-sand rounded-lg p-10 text-center">
          <p className="text-ink/60 text-sm">You need at least one recipe first. <Link to="/recipes/new" className="text-tea-text underline" data-testid="quote-build-recipe-link">Build one now</Link>.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <section>
              <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50 mb-3">1 · Pick the menu</h2>
              <div className="space-y-2" data-testid="quote-menu-list">
                {recipes.map((r) => {
                  const sel = selected[r.id];
                  return (
                    <div key={r.id} data-testid={`quote-menu-item-${r.id}`}
                      className={`rounded-lg border p-4 transition-colors duration-200 ${sel?.on ? "border-tea-hover bg-tea/10" : "border-sand bg-white"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input type="checkbox" data-testid={`quote-select-${r.id}`} checked={!!sel?.on} onChange={() => toggle(r.id)}
                            className="w-5 h-5 accent-[#506042]" />
                          <div>
                            <p className="font-medium text-sm">{r.name}</p>
                            <p className="text-xs text-ink/50 font-mono-num">{gbp(r.costing?.cost_per_portion)}/portion</p>
                          </div>
                        </label>
                        {sel?.on && (
                          <div className="flex items-center gap-2">
                            <input type="number" min="1" data-testid={`quote-portions-${r.id}`} value={sel.portions}
                              onChange={(e) => setSelected((s) => ({ ...s, [r.id]: { ...s[r.id], portions: e.target.value } }))}
                              className="h-10 w-20 px-2 rounded-md border border-sand bg-white text-sm font-mono-num text-right focus:outline-none focus:ring-2 focus:ring-tea-hover/70" />
                            <span className="text-xs text-ink/50">portions</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section>
              <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50 mb-3">2 · Event details</h2>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-ink/50 flex items-center gap-1 mb-1"><Users size={13} /> Guests</label>
                  <input type="number" min="1" data-testid="quote-guest-count-input" className={inputCls} value={guestCount} onChange={(e) => setGuestCount(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-ink/50 flex items-center gap-1 mb-1"><Percent size={13} /> Margin</label>
                  <input type="number" min="0" max="95" data-testid="quote-margin-input" className={inputCls} value={marginPct} onChange={(e) => setMarginPct(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-ink/50 flex items-center gap-1 mb-1"><Truck size={13} /> Travel £</label>
                  <input type="number" min="0" step="0.5" data-testid="quote-travel-input" className={inputCls} value={travelCost} onChange={(e) => setTravelCost(e.target.value)} />
                </div>
              </div>

              <label className="text-xs text-ink/50 flex items-center gap-1 mt-3 mb-1"><UserPlus size={13} /> Additional staff (servers, front of house)</label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <input type="number" min="0" data-testid="quote-staff-count-input" className={inputCls} value={staffCount}
                    onChange={(e) => setStaffCount(e.target.value)} placeholder="Staff" />
                  <p className="text-[10px] text-ink/40 mt-1">Staff</p>
                </div>
                <div>
                  <input type="number" min="0" step="0.5" data-testid="quote-staff-hours-input" className={inputCls} value={staffHours}
                    onChange={(e) => setStaffHours(e.target.value)} placeholder="Hours" />
                  <p className="text-[10px] text-ink/40 mt-1">Hours each</p>
                </div>
                <div>
                  <input type="number" min="0" step="0.5" data-testid="quote-staff-rate-input" className={inputCls} value={staffRate}
                    onChange={(e) => setStaffRate(e.target.value)} placeholder="£/hr" />
                  <p className="text-[10px] text-ink/40 mt-1">£ per hour</p>
                </div>
              </div>
              <button data-testid="quote-labour-rate-button" onClick={() => setLabourRate(settings.labour_rate_per_hour)}
                className="mt-3 flex items-center gap-1.5 text-xs text-ink/50 hover:text-tea-text">
                <Settings2 size={13} /> Labour rate: {gbp(settings.labour_rate_per_hour)}/hr (change)
              </button>
              {labourRate !== null && (
                <div className="mt-2 flex items-center gap-2">
                  <input type="number" step="0.5" data-testid="labour-rate-input" className="h-10 w-28 px-2 rounded-md border border-sand bg-white text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-tea-hover/70"
                    value={labourRate} onChange={(e) => setLabourRate(e.target.value)} />
                  <button data-testid="labour-rate-save" onClick={saveLabourRate} className="h-10 px-4 rounded-md bg-tea text-tea-text text-sm">Save</button>
                </div>
              )}
            </section>
          </div>

          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24 bg-ink text-cream rounded-lg p-6 space-y-5" data-testid="price-floor-panel">
              {!result ? (
                <div className="py-10 text-center">
                  <p className="text-cream/50 text-sm" data-testid="price-floor-empty">Select at least one dish to see your price floor.</p>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-medium text-cream/50">Don't quote below</p>
                    <p className="font-mono-num text-5xl font-semibold text-tea mt-2" data-testid="price-floor-value">{gbp(result.price_floor)}</p>
                    {result.per_guest != null && (
                      <p className="text-sm text-cream/60 mt-1" data-testid="price-per-guest">{gbp(result.per_guest)} per guest · {guestCount} guests</p>
                    )}
                  </div>
                  <div className="space-y-2 text-sm border-t border-cream/15 pt-4">
                    {result.items.map((it) => (
                      <div key={it.recipe_id} className="flex justify-between" data-testid={`quote-breakdown-${it.recipe_id}`}>
                        <span className="text-cream/70">{it.name} × {it.portions}</span>
                        <span className="font-mono-num">{gbp(it.total_cost)}</span>
                      </div>
                    ))}
                    {result.travel_cost > 0 && (
                      <div className="flex justify-between"><span className="text-cream/70">Travel / fuel</span><span className="font-mono-num">{gbp(result.travel_cost)}</span></div>
                    )}
                    {result.staff_cost > 0 && (
                      <div className="flex justify-between" data-testid="quote-staff-cost">
                        <span className="text-cream/70">Event staff ({staffCount} × {staffHours}h)</span>
                        <span className="font-mono-num">{gbp(result.staff_cost)}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2 text-sm border-t border-cream/15 pt-4">
                    <div className="flex justify-between"><span className="text-cream/70">Your true cost</span><span className="font-mono-num" data-testid="quote-total-cost">{gbp(result.total_cost)}</span></div>
                    <div className="flex justify-between"><span className="text-cream/70">Margin at floor ({result.margin_pct}%)</span><span className="font-mono-num text-almond" data-testid="quote-profit">{gbp(result.profit)}</span></div>
                  </div>
                  <p className="text-[11px] text-cream/40 border-t border-cream/15 pt-4">
                    Uses your latest confirmed receipt prices, yield factors, labour at {gbp(settings.labour_rate_per_hour)}/hr and packaging per portion.
                  </p>
                  <button onClick={turnIntoInvoice} data-testid="quote-turn-into-invoice-button"
                    className="w-full h-11 rounded-lg bg-tea hover:bg-tea-hover text-tea-text text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-200">
                    <Receipt size={16} /> Turn into invoice
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
